import { Action, Thunk, action, thunk } from "easy-peasy";
import { post, get, del } from "requests";

export interface OfferStoreModel {
  offers: any[];
  contractTerms: any;
  isLoading: boolean;
  error: string | null;
  averageTrueDeposits: any;
  mcaHoldbackPercentage: any;
  selectedRows: any[];

  setOffers: Action<OfferStoreModel, any[]>;
  setMcaHoldbackPercentage: Action<OfferStoreModel, any>;
  setAverageTrueDeposits: Action<OfferStoreModel, any>;
  setSelectedRows: Action<OfferStoreModel, any>;

  setContractTerms: Action<OfferStoreModel, any[]>;
  setLoading: Action<OfferStoreModel, boolean>;
  setError: Action<OfferStoreModel, string | null>;

  fetchOffers: Thunk<OfferStoreModel, any>;
  fetchRecalculatedApplication: Thunk<OfferStoreModel, any>;

  deleteOffer: Thunk<OfferStoreModel, any>;

  onInit: Thunk<OfferStoreModel, any>;
  refresh: Thunk<OfferStoreModel, any>;
  createOrUpdateOffer: Thunk<OfferStoreModel, any>;
  saveContractTerms: Thunk<OfferStoreModel, any>;
  getContractTerms: Thunk<OfferStoreModel, any>;
  resetStore: Action<OfferStoreModel>;

  handleAcceptedOffer: Thunk<
    OfferStoreModel,
    {
      acceptedOffer: any;
      externalFunders: any[];
      heldbacksData: any[];
      dealApplicationId: number;
      saveHeldback: Function;
      deleteHeldBack: Function;
      saveFinalOffer: Function;
    }
  >;
  clearAcceptedOffer: Thunk<
    OfferStoreModel,
    { dealApplicationId: number; saveFinalOffer: Function }
  >;
  handleSaveOffer: Thunk<
    OfferStoreModel,
    {
      offer: any;
      dealApplicationId: number;
      externalFunders: any[];
      heldbacksData: any[];
      saveHeldback: Function;
      deleteHeldBack: Function;
      saveFinalOffer: Function;
      handleBankAmountUpdate: Function;
      fetchRecalculatedApplication: Function;
      updateSyndicatorsPercentage: Function;
      refreshHeldabckComm: Function;
      refreshSynd: Function;
    }
  >;
}

const OfferStore: OfferStoreModel = {
  offers: [],
  selectedRows: [],
  contractTerms: {},
  averageTrueDeposits: null,
  mcaHoldbackPercentage: 0,
  isLoading: false,
  error: null,

  setMcaHoldbackPercentage: action((state, payload) => {
    state.mcaHoldbackPercentage = payload;
  }),

  setAverageTrueDeposits: action((state, payload) => {
    state.averageTrueDeposits = payload;
  }),

  setOffers: action((state, payload) => {
    state.offers = payload;
  }),

  setSelectedRows: action((state, payload) => {
    state.selectedRows = payload;
  }),

  setContractTerms: action((state, payload) => {
    state.contractTerms = payload;
  }),

  setLoading: action((state, payload) => {
    state.isLoading = payload;
  }),

  setError: action((state, payload) => {
    state.error = payload;
  }),

  resetStore: action((state) => {
    state.offers = [];
    state.contractTerms = {};
    state.error = null;
  }),

  fetchOffers: thunk(async (actions, dealApplicationId) => {
    try {
      const response = await get(`getMultipleOffers/${dealApplicationId}`);
      actions.setOffers(response);
      const offerKeys = response?.map((offer: any) => offer?.offerKey);
      actions.setSelectedRows(offerKeys);
    } catch (error) {
      actions.setError("Failed to fetch offers");
    } finally {
    }
  }),

  fetchRecalculatedApplication: thunk(async (actions, dealApplicationId) => {
    try {
      const response = await get(`fetchRecalculatedDealApplication/${dealApplicationId}`);
    } catch (error) {
      actions.setError("Failed to do recalculation");
    }
  }),

  createOrUpdateOffer: thunk(async (actions, offer) => {
    try {
      const response = await post("saveOffer", offer);
      return response;
    } catch (error) {
      actions.setError("Failed to create offer");
    }
  }),

  deleteOffer: thunk(async (actions, { dealApplicationId, offerId }) => {
    try {
      await del(`deleteOffer/${dealApplicationId}/${offerId}`);
    } catch (error) {
      actions.setError("Failed to delete offer");
    }
  }),

  getContractTerms: thunk(async (actions, dealApplicationId) => {
    try {
      const response = await get(`getContractTerms/${dealApplicationId}`);
      actions.setContractTerms(response);
      return response;
    } catch (error) {
      actions.setError("Failed to fetch contract Terms");
    }
  }),

  saveContractTerms: thunk(async (actions, request) => {
    try {
      if (
        request?.contractTerms &&
        request?.contractTerms?.payingOffDeals &&
        request?.contractTerms?.payingOffDeals.length > 0
      ) {
        request.contractTerms.payingOffDealNos = request?.contractTerms?.payingOffDeals;
      }
      await post(`saveContractTerms`, request);
    } catch (error) {
      actions.setError("Failed to fetch contract Terms");
    }
  }),
  onInit: thunk(async (actions, dealApplicationId) => {
    actions.resetStore();
    await actions.fetchOffers(dealApplicationId);
    const response = await actions.getContractTerms(dealApplicationId);
    return response;
  }),

  refresh: thunk(async (actions, dealApplicationId) => {
    await actions.fetchOffers(dealApplicationId);
    const response = await actions.getContractTerms(dealApplicationId);
    return response;
  }),

  handleAcceptedOffer: thunk(
    async (
      actions,
      {
        acceptedOffer,
        externalFunders,
        heldbacksData,
        dealApplicationId,
        saveHeldback,
        deleteHeldBack,
        saveFinalOffer,
      },
      { getState }
    ) => {
      try {
        const state = getState();
        const newContractTerms = state.contractTerms;
        newContractTerms.applicationId = dealApplicationId;
        newContractTerms.tenantId = 1;
        newContractTerms.bankFee = acceptedOffer?.bankFeeAmount;
        newContractTerms.bankFeePercentage = acceptedOffer?.bankFeePercentage;
        newContractTerms.commissionTotal = acceptedOffer?.commissionAmount;
        newContractTerms.bankFeeCalculationThrough = "bank_fee_percentage";
        newContractTerms.paybackAmountCalculationThrough = "payback_factor";

        newContractTerms.contractTerms.collectionFrequency = acceptedOffer?.frequency;
        newContractTerms.contractTerms.collectionRecurringAmount = acceptedOffer?.recurringAmount;
        newContractTerms.contractTerms.commissionAmount = acceptedOffer?.commissionAmount;
        newContractTerms.contractTerms.commissionPercentage = acceptedOffer?.commissionPercentage;
        newContractTerms.contractTerms.funder = { id: acceptedOffer?.funderId };
        newContractTerms.contractTerms.heldbackAmount = acceptedOffer?.otherFees;
        newContractTerms.contractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;
        newContractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;

        newContractTerms.contractTerms.fundedAmountChange = true;
        newContractTerms.contractTerms.commissionPercentageCalculationThrough =
          "commission_percentage";
        newContractTerms.contractTerms.fundingDate = acceptedOffer?.fundingDate;

        newContractTerms.fundedAmount = acceptedOffer?.fundingAmount;
        newContractTerms.purchaseAmount = acceptedOffer?.paybackAmount;
        newContractTerms.paybackFactor = acceptedOffer?.factor;
        newContractTerms.estimatedPayoffDate = acceptedOffer?.estimatedPayoffDate;
        newContractTerms.contractTerms.receivablePercentage = acceptedOffer?.specifiedPercentage;

        const refiDeals = newContractTerms.contractTerms.payingOffDeals;
        let newRefiDeals: any = [];

        const refinanceAmount = acceptedOffer?.refinanceAmount || 0;

        if (refinanceAmount > 0 && refiDeals && refiDeals.length > 0) {
          const refiDeal = refiDeals[0];
          refiDeal.payingoffAmount = refinanceAmount;
          newRefiDeals = [refiDeal];
        }

        newContractTerms.contractTerms.parentDealAmount = refinanceAmount;
        newContractTerms.contractTerms.payingOffDeals = newRefiDeals;

        if (acceptedOffer.externalPayOffAmount && acceptedOffer.externalPayOffAmount > 0) {
          const defaultExternalFunder = externalFunders[0];
          if (defaultExternalFunder) {
            Object.assign(newContractTerms.contractTerms, {
              externalAbaNumber: defaultExternalFunder.externalAbaNumber,
              externalAccountNumber: defaultExternalFunder.externalAccountNumber,
              externalAmount: acceptedOffer.externalPayOffAmount,
              externalBankName: defaultExternalFunder.externalBankName,
              externalFunderId: defaultExternalFunder.externalFunderId,
              mcaCompany: defaultExternalFunder.mcaCompany,
            });
          }
        } else {
          newContractTerms.contractTerms.externalFunderId = null;
        }

        await saveFinalOffer(newContractTerms);

        const otherFees = heldbacksData.filter((fee: any) => fee.type !== "bank_fee");

        if (otherFees.length > 0) {
          if (acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
            const fee = {
              ...otherFees[0],
              amount: acceptedOffer.otherFees,
              partyId: otherFees[0].applicationPartyId,
            };

            for (let i = 1; i < otherFees.length; i++) {
              await deleteHeldBack({ id: otherFees[i].id, applicationId: dealApplicationId });
            }

            await saveHeldback({
              offerDetails: newContractTerms,
              heldbackData: fee,
              dealApplicationId,
            });
          } else {
            for (const fee of otherFees) {
              await deleteHeldBack({ id: fee.id, applicationId: dealApplicationId });
            }
          }
        } else if (acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
          const fee = {
            amount: acceptedOffer?.otherFees,
            applicationId: dealApplicationId,
            description: "",
            partyId: acceptedOffer?.funderId || newContractTerms?.contractTerms?.funder?.funderId,
            tenantId: "1",
            type: "origination_fee",
          };
          await saveHeldback({
            offerDetails: newContractTerms,
            heldbackData: fee,
            dealApplicationId,
          });
        }
      } catch (e) {
        console.error("Error in handleAcceptedOffer:", e);
      }
    }
  ),

  clearAcceptedOffer: thunk(
    async (actions, { dealApplicationId, saveFinalOffer }, { getState }) => {
      const state = getState();
      const currentContractTerms = state.contractTerms;

      const newContractTerms = {
        ...currentContractTerms,
        contractTerms: { ...currentContractTerms.contractTerms },
      };

      newContractTerms.applicationId = dealApplicationId;
      newContractTerms.tenantId = 1;
      newContractTerms.bankFee = 0;
      newContractTerms.bankFeePercentage = 0;
      newContractTerms.commissionTotal = 0;

      Object.assign(newContractTerms.contractTerms, {
        collectionFrequency: "",
        collectionRecurringAmount: 0,
        commissionAmount: 0,
        commissionPercentage: 0,
        heldbackAmount: 0,
        numberOfDebits: 0,
        receivablePercentage: 0,
      });

      newContractTerms.fundedAmount = 0;
      newContractTerms.purchaseAmount = 0;
      newContractTerms.paybackFactor = 0;
      newContractTerms.estimatedPayoffDate = "";

      await saveFinalOffer(newContractTerms);
    }
  ),

  handleSaveOffer: thunk(
    async (
      actions,
      {
        offer,
        dealApplicationId,
        externalFunders,
        heldbacksData,
        saveHeldback,
        deleteHeldBack,
        saveFinalOffer,
        handleBankAmountUpdate,
        fetchRecalculatedApplication,
        updateSyndicatorsPercentage,
        refreshHeldabckComm,
        refreshSynd,
      }
    ) => {
      try {
        actions.setLoading(true);
        const { oldStatus, ...cleanedOffer } = offer;
        cleanedOffer.applicationId = dealApplicationId;

        const response = await actions.createOrUpdateOffer(cleanedOffer);

        if (response?.acceptedOffer) {
          await actions.handleAcceptedOffer({
            acceptedOffer: response.acceptedOffer,
            externalFunders,
            heldbacksData,
            dealApplicationId,
            saveHeldback,
            deleteHeldBack,
            saveFinalOffer,
          });
        } else if (oldStatus === "Accepted" && cleanedOffer?.status !== "Accepted") {
          await actions.clearAcceptedOffer({ dealApplicationId, saveFinalOffer });
        }

        const offerDetails = await actions.getContractTerms(dealApplicationId);

        await handleBankAmountUpdate({ offerDetails, dealApplicationId });
        await fetchRecalculatedApplication(dealApplicationId);
        await updateSyndicatorsPercentage({ applicationId: dealApplicationId, reload: false });
        await actions.fetchOffers(dealApplicationId);
        await refreshHeldabckComm(dealApplicationId);
        await refreshSynd(dealApplicationId);
      } catch (error) {
        console.error("Error in handleSaveOffer:", error);
        actions.setError("Error saving offer");
      } finally {
        actions.setLoading(false);
      }
    }
  ),
};

export default OfferStore;
